import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { ArrowBack, Backspace, Close, Lock, Search } from '@mui/icons-material'
import API from '../../../services/API'
import { TenantSettingsContext } from '../../../App'

const PIN_LENGTH = 4

const staffUserId = (staff) =>
  staff?.id || staff?._id || staff?.user?._id || staff?.user

const normaliseStaffUser = (staff) => {
  const user = staff?.user || staff
  const id = staffUserId(staff)
  return {
    id,
    _id: id,
    name:
      staff?.name ||
      `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() ||
      'Staff member',
    role: staff?.role || 'Clinician',
    photograph: user?.photograph || staff?.photograph,
    email: user?.email || staff?.email,
  }
}

const PinDots = ({ value }) => (
  <Stack direction="row" justifyContent="center" spacing={1.25} my={2}>
    {Array.from({ length: PIN_LENGTH }).map((_, index) => (
      <Box
        key={index}
        sx={{
          width: 16,
          height: 16,
          borderRadius: '50%',
          backgroundColor: index < value.length ? '#239AA4' : '#E2E8F0',
          border:
            index < value.length ? '2px solid #239AA4' : '2px solid #CBD5E1',
        }}
      />
    ))}
  </Stack>
)

const KeypadButton = ({ children, onClick, color = 'inherit' }) => (
  <Button
    fullWidth
    variant="outlined"
    onClick={onClick}
    sx={{
      height: 64,
      borderRadius: 2,
      fontSize: '1.25rem',
      fontWeight: 900,
      color,
      borderColor: 'rgba(15,23,42,0.12)',
      backgroundColor: '#FFFFFF',
      '&:hover': {
        backgroundColor: '#F8FAFC',
        borderColor: '#239AA4',
      },
    }}
  >
    {children}
  </Button>
)

const SwitchUserDialog = ({
  open,
  onClose,
  onSwitch,
  users = [],
  locationID,
  locked = false,
  countdown,
}) => {
  const [selectedUser, setSelectedUser] = useState(null)
  const [pin, setPin] = useState('')
  const [search, setSearch] = useState('')
  const [mode, setMode] = useState('pin')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [newPin, setNewPin] = useState('')
  const [confirmPin, setConfirmPin] = useState('')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [checkingUser, setCheckingUser] = useState(false)
  const { tenantInfo } = useContext(TenantSettingsContext)

  const staffUsers = useMemo(
    () => users.map(normaliseStaffUser).filter((user) => user.id),
    [users],
  )

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return staffUsers
    return staffUsers.filter((user) =>
      [user.name, user.role, user.email]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(q)),
    )
  }, [search, staffUsers])
  const setupPinIsValid = /^\d{4}$/.test(newPin)
  const setupPinsMatch = newPin === confirmPin
  const canSubmitPassword =
    !!selectedUser &&
    !!username.trim() &&
    !!password &&
    !saving &&
    !checkingUser &&
    (mode !== 'setup' || (setupPinIsValid && setupPinsMatch))

  useEffect(() => {
    if (!open) {
      setSelectedUser(null)
      setPin('')
      setSearch('')
      setMode('pin')
      setUsername('')
      setPassword('')
      setNewPin('')
      setConfirmPin('')
      setError('')
      setSaving(false)
      setCheckingUser(false)
    }
  }, [open])

  const selectUser = async (user) => {
    setSelectedUser(user)
    setMode('pin')
    setPin('')
    setUsername(user.email || '')
    setPassword('')
    setNewPin('')
    setConfirmPin('')
    setError('')
    setCheckingUser(true)

    try {
      const res = await API.post(
        `/commandcontrol/treatment-locations/${locationID}/workstation/status`,
        {
          userID: user.id,
        },
      )

      if (res.data?.requiresPinSetup) {
        setMode('setup')
        setError('Enter your password, then create a 4-digit PIN.')
      }
    } catch (e) {
      setError('Unable to check this staff profile.')
    } finally {
      setCheckingUser(false)
    }
  }

  const addDigit = (digit) => {
    setPin((prev) => (prev.length >= PIN_LENGTH ? prev : `${prev}${digit}`))
  }

  useEffect(() => {
    if (!open || !selectedUser || mode !== 'pin') return undefined

    const handleKeyDown = (e) => {
      if (/^\d$/.test(e.key)) {
        e.preventDefault()
        addDigit(e.key)
      } else if (e.key === 'Backspace') {
        e.preventDefault()
        setPin((prev) => prev.slice(0, -1))
      } else if (e.key === 'Escape') {
        e.preventDefault()
        setPin('')
      } else if (e.key === 'Enter') {
        e.preventDefault()
        unlockWithPin()
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [mode, open, pin, selectedUser, saving])

  const unlockWithPin = async () => {
    if (!selectedUser || pin.length !== PIN_LENGTH || saving) return
    setSaving(true)
    setError('')

    try {
      const res = await API.post(
        `/commandcontrol/treatment-locations/${locationID}/workstation/pin`,
        {
          userID: selectedUser.id,
          pin,
        },
      )

      if (res.status === 200) {
        onSwitch(res.data.user)
        onClose?.()
      }
    } catch (e) {
      if (e.response?.status === 428) {
        setMode('setup')
        setError('Create a PIN before using this workstation.')
      } else if (
        e.response?.status === 423 ||
        e.response?.data?.requiresPassword
      ) {
        setMode('password')
        setError('Too many incorrect PIN attempts. Enter your password.')
      } else {
        setError(
          e.response?.data?.attemptsRemaining !== undefined
            ? `Incorrect PIN. ${e.response.data.attemptsRemaining} attempts remaining.`
            : 'Unable to unlock workstation.',
        )
      }
      setPin('')
    } finally {
      setSaving(false)
    }
  }

  const unlockWithPassword = async () => {
    if (!selectedUser || !username || !password || saving) return
    if (
      mode === 'setup' &&
      (!/^\d{4}$/.test(newPin) || newPin !== confirmPin)
    ) {
      setError('Enter the same 4-digit PIN twice.')
      return
    }

    setSaving(true)
    setError('')

    try {
      const payload = {
        userID: selectedUser.id,
        username: username.trim(),
        password,
      }

      if (mode === 'setup') {
        payload.pin = newPin
        payload.confirmPin = confirmPin
      }

      const res = await API.post(
        `/commandcontrol/treatment-locations/${locationID}/workstation/password`,
        payload,
      )

      if (res.status === 200) {
        onSwitch(res.data.user)
        onClose?.()
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Unable to unlock workstation.')
    } finally {
      setSaving(false)
    }
  }

  const handleBackToUsers = () => {
    setSelectedUser(null)
    setPin('')
    setError('')
    setMode('pin')
  }

  return (
    <Dialog
      open={open}
      onClose={locked ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            px: 2.5,
            py: 2,
            background:
              'linear-gradient(135deg, #242F40 0%, #2c3e50 70%, #239AA4 160%)',
            color: 'white',
          }}
        >
          <Stack spacing={0.25}>
            <Typography fontWeight={900} color="white">
              {locked ? 'Treatment Centre Locked' : 'Switch User'}
            </Typography>
            <Typography variant="caption" color="white" sx={{ opacity: 0.72 }}>
              {locked
                ? 'Select your profile to continue'
                : 'Select your profile and enter your PIN'}
            </Typography>
          </Stack>

          {!locked && (
            <IconButton onClick={onClose} sx={{ color: 'white' }}>
              <Close />
            </IconButton>
          )}
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 2.5, backgroundColor: '#F8FAFC' }}>
        <Box pt={2}>
          {tenantInfo?.isDemo && (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              Demo: every staff member&apos;s PIN is <strong>1234</strong>.
            </Alert>
          )}
          {!selectedUser ? (
            <Stack spacing={1.5} p={1}>
              <TextField
                label="Search staff"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />

              {filteredUsers.length === 0 ? (
                <Typography color="text.secondary">
                  No assigned staff found.
                </Typography>
              ) : (
                filteredUsers.map((user) => (
                  <Paper
                    key={user.id}
                    elevation={0}
                    onClick={() => selectUser(user)}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      cursor: 'pointer',
                      border: '1px solid rgba(15,23,42,0.08)',
                      '&:hover': {
                        backgroundColor: '#FFFFFF',
                        boxShadow: '0 10px 24px rgba(15,23,42,0.08)',
                      },
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar src={user.photograph} sx={{ bgcolor: '#239AA4' }}>
                        {user.name?.[0]}
                      </Avatar>
                      <Box>
                        <Typography fontWeight={900}>{user.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {user.role}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                ))
              )}
            </Stack>
          ) : (
            <Stack spacing={2} p={1}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <IconButton onClick={handleBackToUsers}>
                  <ArrowBack />
                </IconButton>
                <Avatar
                  src={selectedUser.photograph}
                  sx={{ bgcolor: '#239AA4' }}
                >
                  {selectedUser.name?.[0]}
                </Avatar>
                <Box>
                  <Typography fontWeight={900}>{selectedUser.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedUser.role}
                  </Typography>
                </Box>
              </Stack>

              {error && <Alert severity="error">{error}</Alert>}

              {checkingUser ? (
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2 }}>
                  <Stack alignItems="center" spacing={1}>
                    <Lock color="primary" />
                    <Typography fontWeight={900}>
                      Checking profile...
                    </Typography>
                  </Stack>
                </Paper>
              ) : mode === 'pin' ? (
                <>
                  <Paper elevation={0} sx={{ p: 2, borderRadius: 2 }}>
                    <Stack alignItems="center" spacing={1}>
                      <Lock color="primary" />
                      <Typography fontWeight={900}>
                        Enter 4-digit PIN
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Use the keypad or number keys.
                      </Typography>
                      <PinDots value={pin} />
                    </Stack>
                  </Paper>

                  <Grid container spacing={1}>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((number) => (
                      <Grid item xs={4} key={number}>
                        <KeypadButton onClick={() => addDigit(number)}>
                          {number}
                        </KeypadButton>
                      </Grid>
                    ))}
                    <Grid item xs={4}>
                      <KeypadButton onClick={() => setPin('')} color="#64748B">
                        Clear
                      </KeypadButton>
                    </Grid>
                    <Grid item xs={4}>
                      <KeypadButton onClick={() => addDigit(0)}>0</KeypadButton>
                    </Grid>
                    <Grid item xs={4}>
                      <KeypadButton
                        onClick={() => setPin((prev) => prev.slice(0, -1))}
                        color="#E32525"
                      >
                        <Backspace />
                      </KeypadButton>
                    </Grid>
                  </Grid>
                </>
              ) : (
                <Stack spacing={1.5}>
                  <TextField
                    label="Username / email"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && mode === 'password') {
                        unlockWithPassword()
                      }
                    }}
                    fullWidth
                  />
                  {mode === 'setup' && (
                    <>
                      <TextField
                        label="Create 4-digit PIN"
                        type="password"
                        inputProps={{
                          inputMode: 'numeric',
                          maxLength: PIN_LENGTH,
                        }}
                        value={newPin}
                        onChange={(e) =>
                          setNewPin(
                            e.target.value.replace(/\D/g, '').slice(0, 4),
                          )
                        }
                        error={!!newPin && !setupPinIsValid}
                        helperText={
                          newPin && !setupPinIsValid
                            ? 'PIN must be exactly 4 digits.'
                            : ' '
                        }
                        fullWidth
                      />
                      <TextField
                        label="Confirm PIN"
                        type="password"
                        inputProps={{
                          inputMode: 'numeric',
                          maxLength: PIN_LENGTH,
                        }}
                        value={confirmPin}
                        onChange={(e) =>
                          setConfirmPin(
                            e.target.value.replace(/\D/g, '').slice(0, 4),
                          )
                        }
                        error={!!confirmPin && !setupPinsMatch}
                        helperText={
                          confirmPin && !setupPinsMatch
                            ? 'PINs do not match.'
                            : ' '
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            unlockWithPassword()
                          }
                        }}
                        fullWidth
                      />
                    </>
                  )}
                </Stack>
              )}
            </Stack>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 2 }}>
        {!locked && (
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
        )}
        {selectedUser && mode === 'pin' && (
          <Button
            variant="contained"
            onClick={unlockWithPin}
            disabled={checkingUser || pin.length !== PIN_LENGTH || saving}
          >
            {saving ? 'Checking...' : 'Unlock'}
          </Button>
        )}
        {selectedUser && mode !== 'pin' && (
          <Button
            variant="contained"
            onClick={unlockWithPassword}
            disabled={!canSubmitPassword}
          >
            {saving
              ? 'Checking...'
              : mode === 'setup'
                ? 'Create PIN'
                : 'Unlock'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default SwitchUserDialog
